<template>
  <Layout>
    <Teleport to="body">
      <div
        v-if="showBlockedModal"
        class="modal fade show"
        style="display: block"
      >
        <div class="modal-dialog login-modal-dialog">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title">{{ t('blockedTitle') }}</h5>
              <button
                type="button"
                class="btn-close"
                :aria-label="t('common.closeAriaLabel')"
                @click="closeBlockedModal"
              />
            </div>
            <div class="modal-body">
              {{ t('blockedBody') }}
            </div>
          </div>
        </div>
      </div>
      <div
        v-if="showBlockedModal"
        class="modal-backdrop fade show"
      ></div>
    </Teleport>

    <template v-if="$route.query.ext !== undefined">
      <External />

      <div class="d-grid gap-2 mb-2 text-center">
        <RouterLink
          :to="{ name: 'login' }"
          class="btn btn-link bg-transparent"
        >
          <i class="bi bi-arrow-left-circle me-2"></i>
          {{ t('common.back') }}
        </RouterLink>
      </div>
    </template>
    <template v-else>
      <div class="login_title mt-4 ms-2 mb-2">{{ t('chooseAccount') }}</div>
      <a
        href="#"
        @click.prevent="startEmployeeLogin"
      >
        <div class="login_box login_employee">
          <i class="bi bi-microsoft me-2"></i>
          {{ t('employee') }}
        </div>
      </a>
      <RouterLink :to="{ name: 'login', query: { ext: 1 } }">
        <div class="login_box login_other">
          <i class="bi bi-plus-circle me-2"></i>
          {{ t('externalUser') }}
        </div>
      </RouterLink>
    </template>
  </Layout>
</template>

<style lang="scss">
.bg_body {
  background: url(@/assets/back3.jpg) center center no-repeat;
  background-size: cover;
  height: 100%;
  width: 100%;
}
.login_head {
  padding: 1em;
  text-align: center;
}
.login_box {
  text-align: center;
  background-color: #e9ecef;
  padding: 20px;
  margin: 1em 0;
  text-decoration: none;
}
.logo_login {
  height: 50px;
}
.login_table {
  width: 95%;
  margin: 5em auto;
  max-width: 500px;
  min-width: 280px;
}
.login_table a {
  text-decoration: none;
}
.login_table form {
  padding: 1em;
}
.login_table form button {
  padding: 0.3em 2em;
}
.login-modal-dialog {
  max-width: 500px;
  width: 95%;
  margin: 5em auto;
}
.footer {
  text-align: right;
  margin: 2em 1em 1em 0;
}
.logout-button {
  float: right;
}
</style>

<script setup>
import { onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import { storeToRefs } from 'pinia'
import { useApi } from '@/composables/api'
import { useCookies } from '@vueuse/integrations/useCookies'
import { useMessageStack } from '@/stores/messagestack'
import { useUiStore } from '@/stores/ui'
import config from '@/config'
import Layout from './login/layout.vue'
import External from './login/external.vue'

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const { returnUrl } = storeToRefs(useUiStore())
const { push: openModal } = useMessageStack()
const { model: sso, refresh: getSso } = useApi({
  url: '/web/login',
})
const { set: setCookue } = useCookies(['service'])

const showBlockedModal = ref(false)
let loginErrorHandled = false
const employeeLoginPending = ref(false)

function isBlockedLoginError(raw) {
  const s = String(raw ?? '').toLowerCase()
  return s.includes('forbidden') || s.includes('not active') || s.includes('inactive') || s.includes('неактив')
}

function closeBlockedModal() {
  showBlockedModal.value = false
}

async function consumeLoginError(raw) {
  if (loginErrorHandled || raw == null || raw === '') {
    return
  }
  loginErrorHandled = true
  const loginError = Array.isArray(raw) ? raw[0] : raw
  if (isBlockedLoginError(loginError)) {
    showBlockedModal.value = true
  } else {
    // Toast: Login в app.vue монтируется до готовности route.query после SSO-редиректа.
    await openModal({ type: 'error', message: String(loginError) })
  }
  // Убираем login_error из URL, чтобы уведомление не повторялось при обновлении страницы
  const nextQuery = { ...route.query }
  delete nextQuery.login_error
  await router.replace({ name: 'login', query: nextQuery })
}

watch(
  () => route.query.login_error,
  raw => {
    consumeLoginError(raw)
  },
  { immediate: true },
)

function setServiceCookie() {
  setCookue('service', 'rtms', {
    domain: config.VITE_PRIMARY_DOMAIN,
    path: '/',
    sameSite: 'lax',
  })
}

function ssoStartQuery() {
  const ru = returnUrl.value
  if (ru && typeof ru === 'object' && ru.fullPath && !['login', 'sso_login'].includes(ru.name)) {
    return { next: ru.fullPath }
  }
  return {}
}

async function startEmployeeLogin() {
  if (employeeLoginPending.value) {
    return
  }
  employeeLoginPending.value = true
  try {
    setServiceCookie()
    const q = ssoStartQuery()
    await getSso(Object.keys(q).length ? { query: q } : {})
    const uri = sso.value?.auth_uri
    if (uri) {
      window.location.href = uri
    } else {
      await openModal({ type: 'error', message: t('microsoftSignInError') })
    }
  } catch {
    await openModal({ type: 'error', message: t('microsoftSignInError') })
  } finally {
    employeeLoginPending.value = false
  }
}

onMounted(async () => {
  setServiceCookie()
  const q = ssoStartQuery()
  await getSso(Object.keys(q).length ? { query: q } : {})
})
</script>

<i18n>
{
  "ru": {
    "blockedTitle": "Доступ к системе отсутствует",
    "blockedBody": "Доступ к системе отсутствует, обратитесь к администратору",
    "chooseAccount": "Выберите аккаунт:",
    "employee": "Сотрудник АстраЗенека",
    "externalUser": "Внешний пользователь",
    "microsoftSignInError": "Не удалось начать вход через Microsoft. Повторите попытку."
  },
  "en": {
    "blockedTitle": "Access to the system is unavailable",
    "blockedBody": "Access to the system is unavailable, please contact an administrator.",
    "chooseAccount": "Choose an account:",
    "employee": "AstraZeneca employee",
    "externalUser": "External user",
    "microsoftSignInError": "Could not start Microsoft sign-in. Please try again."
  }
}
</i18n>
